import React, { useState, useEffect, useRef, useContext } from "react";
import * as Yup from "yup";
import { Formik, Form, Field, useFormikContext } from "formik";
import { toast } from "react-toastify";
import { isNil } from "lodash";
import clsx from "clsx";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";

import { makeStyles } from "@material-ui/core/styles";
import moment from "moment";
import {
  Dialog,
  DialogContent,
  Button,
  DialogActions,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Switch,
  Tab,
  Tabs,
  Chip,
  Tooltip,
} from "@material-ui/core";

import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import toastError from "../../errors/toastError";
import ChannelRoutingPanel from "../ChannelRoutingPanel";
import ChannelMessageField from "../ChannelMessageField";
import TabPanel from "../TabPanel";
import { Autorenew, FileCopy, HeadsetMicOutlined, AccountTreeOutlined, ChatBubbleOutlineRounded, VpnKeyOutlined, ArrowForwardRounded } from "@material-ui/icons";
import useCompanySettings from "../../hooks/useSettings/companySettings";
import SchedulesForm from "../SchedulesForm";
import usePlans from "../../hooks/usePlans";
import { AuthContext } from "../../context/Auth/AuthContext";
import { Colorize } from "@material-ui/icons";
import ColorPicker from "../ColorPicker";
import InputAdornment from "@material-ui/core/InputAdornment";
import IconButton from "@material-ui/core/IconButton";
import getRandomHexColor from "../../utils/getRandomHexColor";
import MetaBusinessAssets from "../MetaBusinessAssets";
import StatusBadge from "../ui/StatusBadge";
import { getTokens, brandAlpha } from "../../styles/tokens";
import { usePanelStyles, ModalTitle, FormSection, SettingRow } from "../FormFields/panel";
import { isWebchat as isWebchatChannel } from "../../utils/channels";
import WebchatSettingsPanel from "../WebchatSettingsPanel";

const channelSections = {
  general: ["Identificação", "Identifique o canal e ajuste como ele participa do atendimento."],
  credentials: ["API oficial · Meta", "Gerencie as credenciais e o número conectado à sua empresa."],
  integrations: ["Filas e integrações", "Escolha as filas que atendem neste canal e as integrações utilizadas."],
  flowbuilder: ["Fluxos de conversa", "Defina o chatbot que recebe os contatos. Os fluxos são criados no editor de Fluxos de conversa."],
  messages: ["Mensagens automáticas", "Personalize as mensagens e a mídia enviadas por este canal."],
  chatbot: ["Regras de atendimento", "Ajuste limites do menu, inatividade, encaminhamento e encerramento."],
  nps: ["Avaliação do atendimento", "Configure a mensagem e os limites da pesquisa de satisfação."],
  schedules: ["Horários", "Defina em quais dias e horários sua equipe atende neste canal."],
  advanced: ["Opções avançadas", "Chave da API deste canal e importação de mensagens, quando disponível."],
};

const attendanceSections = ["integrations", "chatbot", "nps", "schedules"];
const navigationSections = [
  ["general", "Geral"],
  ["integrations", "Atendimento"],
  ["flowbuilder", "Fluxos"],
  ["messages", "Mensagens"],
  ["credentials", "API oficial"],
  ["advanced", "Avançado"],
];

function ChannelValidationNavigation({ onNavigate }) {
  const { submitCount, isValidating, isSubmitting, errors } = useFormikContext();
  const handled = useRef(0);
  useEffect(() => {
    if (!submitCount || isValidating || isSubmitting || handled.current === submitCount || !Object.keys(errors).length) return;
    handled.current = submitCount;
    if (errors.name) onNavigate("general");
    else if (errors.timeSendQueue || errors.sendIdQueue) onNavigate("integrations");
    else if (["business_id", "send_token", "waba_id", "phone_number_id", "phone_number"].some(key => errors[key])) onNavigate("credentials");
  }, [submitCount, isValidating, isSubmitting, errors, onNavigate]);
  return null;
}

const useStyles = makeStyles((theme) => {
  const t = getTokens(theme.palette.type, theme.palette.primary.main);
  const danger = t.mode === "dark" ? t.status.down.fg : t.semantic.error;
  return {
    root: {
      display: "flex",
      flexWrap: "wrap",
      gap: 4,
    },
    // altura estável: trocar de aba não faz o modal mudar de tamanho
    dialog: {
      height: "min(780px, calc(100% - 64px))",
      [theme.breakpoints.down("xs")]: { height: "calc(100% - 24px)" },
    },
    titleBadge: { marginLeft: 8, verticalAlign: "middle" },
    alert: {
      flex: "none",
      padding: "9px 24px",
      fontFamily: t.font,
      fontSize: 13,
      fontWeight: 500,
      lineHeight: 1.5,
      color: danger,
      backgroundColor: brandAlpha(t.semantic.error, t.mode === "dark" ? 0.16 : 0.07),
      borderBottom: `1px solid ${t.line}`,
      [theme.breakpoints.down("xs")]: { padding: "9px 16px" },
    },
    body: { ...theme.scrollbarStyles },
    // subopções da aba "Atendimento"
    subnav: {
      display: "flex",
      flexWrap: "wrap",
      gap: 6,
      marginBottom: 16,
      "& .MuiButton-root": {
        height: 32,
        minHeight: 32,
        padding: "0 12px",
        fontSize: 12.5,
        fontWeight: 600,
        color: t.textSecondary,
        border: `1px solid ${t.line}`,
        backgroundColor: t.paper,
      },
      "& .MuiButton-root:hover": { color: t.text, borderColor: t.lineStrong },
      "& .MuiButton-root[aria-pressed=true]": {
        color: t.accent,
        borderColor: t.accent,
        backgroundColor: t.accentTint.soft,
      },
    },
    lead: {
      margin: "0 0 18px",
      fontFamily: t.font,
      fontSize: 13,
      lineHeight: 1.55,
      color: t.textSecondary,
    },
    leadAction: { margin: "-4px 0 20px" },
    loading: {
      display: "flex",
      alignItems: "center",
      gap: 10,
      padding: "8px 0",
      fontSize: 13,
      color: t.textSecondary,
    },
    nameGrid: {
      display: "grid",
      gridTemplateColumns: "minmax(0, 1fr) 220px",
      gap: "14px 16px",
      alignItems: "start",
      [theme.breakpoints.down("xs")]: { gridTemplateColumns: "minmax(0, 1fr)" },
    },
    colorSwatch: {
      display: "inline-block",
      width: 18,
      height: 18,
      borderRadius: 4,
      boxShadow: `inset 0 0 0 1px ${brandAlpha("#000000", 0.14)}`,
    },
    shortcuts: {
      display: "grid",
      gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
      gap: 10,
      "& .MuiButton-root": {
        height: "auto",
        minHeight: 56,
        padding: "10px 14px",
        justifyContent: "flex-start",
        textAlign: "left",
        fontWeight: 400,
        color: t.text,
        border: `1px solid ${t.line}`,
        backgroundColor: t.paper,
      },
      "& .MuiButton-root:hover": { borderColor: t.lineStrong, backgroundColor: t.muted },
      "& .MuiButton-label": { display: "flex", alignItems: "center", gap: 12, minWidth: 0 },
      "& .MuiButton-label > svg": { flex: "none", fontSize: 20, color: t.textSecondary },
      "& .MuiButton-label > svg:last-child": { fontSize: 17, color: t.textMuted },
      "& strong": { display: "block", fontSize: 13, fontWeight: 600, lineHeight: 1.4 },
      "& small": { display: "block", marginTop: 2, fontSize: 12, lineHeight: 1.45, color: t.textSecondary },
      [theme.breakpoints.down("xs")]: { gridTemplateColumns: "minmax(0, 1fr)" },
    },
    shortcutText: { flex: 1, minWidth: 0 },
    // campo + ação ao lado (chave da API, webhook, PIN): a ação alinha pela base do campo
    inlineRow: {
      display: "flex",
      alignItems: "flex-end",
      gap: 6,
      minWidth: 0,
      "& > .MuiFormControl-root": { flex: "1 1 auto", minWidth: 0, margin: 0 },
      "& > .MuiButton-root": { flex: "none" },
      "& .MuiIconButton-root": { flex: "none", width: 40, height: 40, padding: 0, color: t.textSecondary },
      "& .MuiIconButton-root:hover": { color: t.text },
    },
    wrapOnPhone: {
      [theme.breakpoints.down("xs")]: { flexWrap: "wrap", "& > .MuiFormControl-root": { flexBasis: "100%" } },
    },
    copied: { color: `${t.accent} !important` },
    spaced: { marginTop: 14 },
    metaBox: {
      marginBottom: 22,
      "& strong": { display: "block", fontSize: 13, fontWeight: 600, color: t.text },
      "& span": { display: "block", marginTop: 2, fontSize: 13, color: t.text },
      "& small": { display: "block", marginTop: 6, fontSize: 12, lineHeight: 1.5, color: t.textSecondary },
    },
    details: {
      marginTop: 14,
      paddingTop: 12,
      borderTop: `1px solid ${t.lineRow}`,
      "& summary": { cursor: "pointer", fontFamily: t.font, fontSize: 13, fontWeight: 600, color: t.accent },
      "& p": { margin: "8px 0 0", fontSize: 12.5, lineHeight: 1.55, color: t.textSecondary },
    },
    importAlert: {
      marginTop: 14,
      padding: "10px 12px",
      borderRadius: t.radius.sm,
      border: `1px solid ${brandAlpha(t.semantic.error, 0.3)}`,
      backgroundColor: brandAlpha(t.semantic.error, t.mode === "dark" ? 0.16 : 0.06),
      fontFamily: t.font,
      fontSize: 12.5,
      lineHeight: 1.55,
      color: danger,
    },
    uploadRow: {
      display: "flex",
      alignItems: "center",
      flexWrap: "wrap",
      gap: 10,
      minWidth: 0,
      "& .MuiChip-root": { maxWidth: "100%" },
    },
    btnWrapper: {
      position: "relative",
    },
    buttonProgress: {
      color: t.accent,
      position: "absolute",
      top: "50%",
      left: "50%",
      marginTop: -12,
      marginLeft: -12,
    },
    saveHint: {
      marginRight: "auto",
      fontFamily: t.font,
      fontSize: 12,
      color: t.textSecondary,
      [theme.breakpoints.down("xs")]: { display: "none" },
    },
  };
});

const SessionSchema = Yup.object().shape({
  timeSendQueue: Yup.number().transform((value, original) => original == null ? 0 : value).typeError("Informe um número de minutos").integer("Use minutos inteiros").min(0, "O tempo não pode ser negativo"),
  sendIdQueue: Yup.number().nullable().transform((value, original) => original === "" || original == null ? null : value).when("timeSendQueue", { is: value => Number(value) > 0, then: schema => schema.required("Escolha a fila de destino").min(1, "Escolha a fila de destino") }),
  name: Yup.string()
    .min(2, "Muito curto! Mínimo 2 caracteres")
    .max(50, "Muito longo! Máximo 50 caracteres")
    .required("O nome da conexão é obrigatório"),
});

// Validação completa para conexões da API Oficial (Meta Business)
const SessionSchemaOficial = SessionSchema.shape({
  phone_number_id: Yup.string()
    .matches(/^\d+$/, "Apenas números — copie o Phone Number ID da Meta")
    .min(10, "ID muito curto — confira na Meta Business")
    .required("Obrigatório: encontre em Meta for Developers → WhatsApp → Configuração da API"),
  waba_id: Yup.string()
    .matches(/^\d+$/, "Apenas números — copie o WhatsApp Business Account ID")
    .min(10, "ID muito curto — confira na Meta Business")
    .required("Obrigatório: ID da conta WhatsApp Business (WABA)"),
  business_id: Yup.string()
    .matches(/^\d+$/, "Apenas números — copie o Business ID do Gerenciador de Negócios")
    .min(10, "ID muito curto — confira na Meta Business")
    .required("Obrigatório: ID do portfólio empresarial na Meta"),
  phone_number: Yup.string()
    .matches(/^\+?\d{10,15}$/, "Formato: DDI+DDD+número, só dígitos. Ex.: 5511999998888")
    .required("Obrigatório: número completo com código do país"),
  send_token: Yup.string()
    .min(50, "Token muito curto — copie o token permanente completo")
    .required("Obrigatório: token de acesso permanente do usuário do sistema"),
});

const WhatsAppModal = ({ open, onClose, whatsAppId, channel, initialSection = "general" }) => {
  const classes = useStyles();
  const p = usePanelStyles();
  const [loadingChannel, setLoadingChannel] = useState(false);
  const [autoToken, setAutoToken] = useState("");

  const inputFileRef = useRef(null);

  const [attachment, setAttachment] = useState(null);
  const [attachmentName, setAttachmentName] = useState("");

  const initialState = {
    name: "",
    greetingMessage: "",
    complationMessage: "",
    outOfHoursMessage: "",
    ratingMessage: "",
    isDefault: false,
    token: "",
    maxUseBotQueues: 3,
    provider: "beta",
    expiresTicket: 0,
    allowGroup: false,
    enableImportMessage: false,
    groupAsTicket: "disabled",
    timeUseBotQueues: "0",
    timeSendQueue: "0",
    sendIdQueue: 0,
    expiresTicketNPS: "0",
    expiresInactiveMessage: "",
    timeInactiveMessage: "",
    inactiveMessage: "",
    maxUseBotQueuesNPS: 3,
    whenExpiresTicket: 0,
    timeCreateNewTicket: 0,
    greetingMediaAttachment: "",
    importRecentMessages: "",
    importOldMessages: "",
    importOldMessagesGroups: "",
    integrationId: "",
    collectiveVacationEnd: "",
    collectiveVacationStart: "",
    collectiveVacationMessage: "",
    queueIdImportMessages: null,
    isOficial: false,
    phone_number_id: "",
    waba_id: "",
    send_token: "",
    business_id: "",
    phone_number: "",
    color: getRandomHexColor(),
    flowInactiveTime: 0,
    flowIdInactiveTime: 0,
    timeAwaitActiveFlowId: 0,
    maxUseInactiveTime: 1,
    timeToReturnQueue: 0,
    triggerIntegrationOnClose: true,
    wavoip: "",
    webchatSettings: { title: "", welcomeText: "", requirePhone: true },
  };
  const [whatsApp, setWhatsApp] = useState(initialState);
  const [selectedQueueIds, setSelectedQueueIds] = useState([]);
  const [queues, setQueues] = useState([]);
  const [tab, setTab] = useState("general");
  const [metaPin, setMetaPin] = useState(""); // PIN de duas etapas (conexão criada pela Meta)
  const [enableImportMessage, setEnableImportMessage] = useState(false);
  const [importOldMessagesGroups, setImportOldMessagesGroups] = useState(false);
  const [closedTicketsPostImported, setClosedTicketsPostImported] =
    useState(false);
  const [importOldMessages, setImportOldMessages] = useState(
    moment().add(-1, "days").format("YYYY-MM-DDTHH:mm")
  );
  const [importRecentMessages, setImportRecentMessages] = useState(
    moment().add(-1, "minutes").format("YYYY-MM-DDTHH:mm")
  );
  const [copied, setCopied] = useState(false);
  const [integrations, setIntegrations] = useState([]);
  const [schedulesEnabled, setSchedulesEnabled] = useState(false);
  const [NPSEnabled, setNPSEnabled] = useState(false);
  const [showOpenAi, setShowOpenAi] = useState(false);
  const [showIntegrations, setShowIntegrations] = useState(false);
  const { user } = useContext(AuthContext);
  const [isOficial, setIsOficial] = useState(false);
  const [useWhatsappOfficial, setUseWhatsappOfficial] = useState(false);
  const [colorPickerModalOpen, setColorPickerModalOpen] = useState(false);

  const [schedules, setSchedules] = useState([
    {
      weekday: i18n.t("queueModal.serviceHours.monday"),
      weekdayEn: "monday",
      startTimeA: "08:00",
      endTimeA: "12:00",
      startTimeB: "13:00",
      endTimeB: "18:00",
    },
    {
      weekday: i18n.t("queueModal.serviceHours.tuesday"),
      weekdayEn: "tuesday",
      startTimeA: "08:00",
      endTimeA: "12:00",
      startTimeB: "13:00",
      endTimeB: "18:00",
    },
    {
      weekday: i18n.t("queueModal.serviceHours.wednesday"),
      weekdayEn: "wednesday",
      startTimeA: "08:00",
      endTimeA: "12:00",
      startTimeB: "13:00",
      endTimeB: "18:00",
    },
    {
      weekday: i18n.t("queueModal.serviceHours.thursday"),
      weekdayEn: "thursday",
      startTimeA: "08:00",
      endTimeA: "12:00",
      startTimeB: "13:00",
      endTimeB: "18:00",
    },
    {
      weekday: i18n.t("queueModal.serviceHours.friday"),
      weekdayEn: "friday",
      startTimeA: "08:00",
      endTimeA: "12:00",
      startTimeB: "13:00",
      endTimeB: "18:00",
    },
    {
      weekday: "Sábado",
      weekdayEn: "saturday",
      startTimeA: "08:00",
      endTimeA: "12:00",
      startTimeB: "13:00",
      endTimeB: "18:00",
    },
    {
      weekday: "Domingo",
      weekdayEn: "sunday",
      startTimeA: "08:00",
      endTimeA: "12:00",
      startTimeB: "13:00",
      endTimeB: "18:00",
    },
  ]);

  const { get: getSetting } = useCompanySettings();
  const { getPlanCompany } = usePlans();

  const [selectedPrompt, setSelectedPrompt] = useState(null);
  const [triggerIntegrationOnClose, setTriggerIntegrationOnClose] =
    useState(true);
  const [integrationType, setIntegrationType] = useState("n8n");
  const [integrationTypeId, setIntegrationTypeId] = useState(null);

  const [prompts, setPrompts] = useState([]);

  const [webhooks, setWebhooks] = useState([]);
  const [flowIdNotPhrase, setFlowIdNotPhrase] = useState();
  const [flowIdWelcome, setFlowIdWelcome] = useState();
  const [flowIdInactiveTime, setFlowIdInactiveTime] = useState();
  const [timeAwaitActiveFlowId, setTimeAwaitActiveFlowId] = useState();
  const [showWavoipCall, setShowWavoipCall] = useState(false);

  useEffect(() => {
    if (!whatsAppId && !whatsApp.token) {
      setAutoToken(generateRandomCode(30));
    } else if (whatsAppId && !whatsApp.token) {
      setAutoToken(generateRandomCode(30));
    } else {
      setAutoToken(whatsApp.token);
    }
  }, [whatsAppId, whatsApp.token]);

  useEffect(() => {
    async function fetchData() {
      const companyId = user.companyId;
      const planConfigs = await getPlanCompany(undefined, companyId);

      setShowOpenAi(planConfigs.plan.useOpenAi);
      setShowIntegrations(planConfigs.plan.useIntegrations);
      setUseWhatsappOfficial(planConfigs.plan.useWhatsappOfficial);
      setShowWavoipCall(planConfigs.plan.wavoip);
    }
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/prompt");
        setPrompts(data.prompts);
      } catch (err) {
        toastError(err);
      }
    })();
  }, [whatsAppId]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/flowbuilder");
        setWebhooks(data.flows);
      } catch (err) {
        toastError(err);
      }
    })();
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      const settingSchedules = await getSetting({
        column: "scheduleType",
      });
      setSchedulesEnabled(settingSchedules.scheduleType === "connection");
      const settingNPS = await getSetting({
        column: "userRating",
      });
      setNPSEnabled(settingNPS.userRating === "enabled");
    };
    fetchData();
  }, []);

  const handleEnableImportMessage = async (e) => {
    setEnableImportMessage(e.target.checked);
  };

  const handleEnableIsOficial = async (e) => {
    setIsOficial(e.target.checked);
  };

  // The selected channel also controls new connections, before an ID exists.
  useEffect(() => {
    setIsOficial(channel === "whatsapp_oficial");
  }, [channel]);

  const isWebchat = isWebchatChannel(channel);

  useEffect(() => {
    if (!open) return;
    let active = true;
    setTab(initialSection);
    setFlowIdNotPhrase(null); setFlowIdWelcome(null); setFlowIdInactiveTime(null); setTimeAwaitActiveFlowId(null);
    setSelectedQueueIds([]); setSelectedPrompt(null); setIntegrationTypeId(null); setTriggerIntegrationOnClose(true);
    setEnableImportMessage(false); setImportOldMessagesGroups(false); setClosedTicketsPostImported(false);
    setAttachment(null); setAttachmentName(""); setCopied(false);
    setWhatsApp(initialState);
    setLoadingChannel(Boolean(whatsAppId));
    if (!whatsAppId) { setSchedules([]); return () => { active = false; }; }
    api.get(`whatsapp/${whatsAppId}?session=0`).then(({ data }) => {
      if (!active) return;
      setWhatsApp({ ...initialState, ...data });
      setFlowIdNotPhrase(data.flowIdNotPhrase || null); setFlowIdWelcome(data.flowIdWelcome || null);
      setFlowIdInactiveTime(data.flowIdInactiveTime || null); setTimeAwaitActiveFlowId(data.timeAwaitActiveFlowId || null);
      setAttachmentName(data.greetingMediaAttachment); setAutoToken(data.token);
      setSelectedPrompt(data.promptId || null); setSelectedQueueIds((data.queues || []).map(queue => queue.id));
      setSchedules(data.schedules || []);
      setTriggerIntegrationOnClose(data.triggerIntegrationOnClose == null ? true : data.triggerIntegrationOnClose);
      setIntegrationTypeId(data.integrationTypeId || null);
      if (!isNil(data.importOldMessages)) {
        setEnableImportMessage(true); setImportOldMessages(data.importOldMessages); setImportRecentMessages(data.importRecentMessages);
        setClosedTicketsPostImported(Boolean(data.closedTicketsPostImported)); setImportOldMessagesGroups(Boolean(data.importOldMessagesGroups));
      }
      setLoadingChannel(false);
    }).catch(err => { if (active) { toastError(err); onClose(); } });
    return () => { active = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, whatsAppId, channel, initialSection]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/queue");
        setQueues(data);
      } catch (err) {
        toastError(err);
      }
    })();
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/queueIntegration");

        setIntegrations(data.queueIntegrations);
      } catch (err) {
        toastError(err);
      }
    })();
  }, []);

  const handleChangeQueue = (e) => {
    setSelectedQueueIds(e);
    setSelectedPrompt(null);
  };

  const handleChangePrompt = (e) => {
    setSelectedPrompt(e.target.value);
    setSelectedQueueIds([]);
  };

  const handleChange = (e) => {
    setTriggerIntegrationOnClose(e.target.value);
  };

  const handleIntegrationTypeChange = (e) => {
    setIntegrationType(e.target.value);
    setIntegrationTypeId(e.target.value);
  };

  const handleSaveWhatsApp = async (values) => {

    if (NPSEnabled) {
      if (isNil(values.ratingMessage)) {
        toastError(i18n.t("whatsappModal.errorRatingMessage"));
        return;
      }

      if (
        values.expiresTicketNPS === "0" &&
        values.expiresTicketNPS === "" &&
        values.expiresTicketNPS === 0
      ) {
        toastError(i18n.t("whatsappModal.errorExpiresNPS"));
        return;
      }
    }

    if (values.timeSendQueue === "") values.timeSendQueue = "0";

    if (
      (values.sendIdQueue === 0 ||
        values.sendIdQueue === "" ||
        isNil(values.sendIdQueue)) &&
      values.timeSendQueue !== 0 &&
      values.timeSendQueue !== "0"
    ) {
      toastError(i18n.t("whatsappModal.errorSendQueue"));
      return;
    }

    const whatsappData = {
      ...values,
      flowIdWelcome: flowIdWelcome ? flowIdWelcome : null,
      flowIdInactiveTime: flowIdInactiveTime ? flowIdInactiveTime : null,
      flowIdNotPhrase: flowIdNotPhrase ? flowIdNotPhrase : null,
      timeAwaitActiveFlowId: timeAwaitActiveFlowId
        ? timeAwaitActiveFlowId
        : null,
      queueIds: selectedQueueIds,
      importOldMessages: enableImportMessage ? importOldMessages : null,
      importRecentMessages: enableImportMessage ? importRecentMessages : null,
      importOldMessagesGroups: importOldMessagesGroups
        ? importOldMessagesGroups
        : null,
      closedTicketsPostImported: closedTicketsPostImported
        ? closedTicketsPostImported
        : null,
      token: autoToken ? autoToken : null,
      schedules,
      promptId: selectedPrompt ? selectedPrompt : null,
      channel,
      triggerIntegrationOnClose: triggerIntegrationOnClose,
      integrationTypeId: triggerIntegrationOnClose ? integrationTypeId : null,
      color: values.color ? values.color : getRandomHexColor(),
      wavoip: values.wavoip ? values.wavoip : null,
    };
    delete whatsappData["queues"];
    delete whatsappData["session"];
    if (!isWebchat) {
      // as colunas do webchat só valem para o canal webchat: nas demais conexões o corpo segue igual ao de antes
      // (o GET devolve webchatCode/webchatSettings nulos e o initialState semeia webchatSettings)
      delete whatsappData["webchatSettings"];
      delete whatsappData["webchatCode"];
    }
    if (isWebchat) {
      // o webchat não tem sessão do WhatsApp, chave da API, canal padrão nem importação
      delete whatsappData["webchatCode"];
      delete whatsappData["token"];
      delete whatsappData["isDefault"];
      delete whatsappData["importOldMessages"];
      delete whatsappData["importRecentMessages"];
      delete whatsappData["importOldMessagesGroups"];
      delete whatsappData["closedTicketsPostImported"];
    }

    try {
      if (whatsAppId) {
        if (
          whatsAppId &&
          !isWebchat &&
          enableImportMessage &&
          whatsApp?.status === "CONNECTED"
        ) {
          try {
            setWhatsApp({ ...whatsApp, status: "qrcode" });
            await api.delete(`/whatsappsession/${whatsApp.id}`);
          } catch (err) {
            toastError(err);
          }
        }

        await api.put(`/whatsapp/${whatsAppId}`, whatsappData);
        if (attachment != null) {
          const formData = new FormData();
          formData.append("file", attachment);
          await api.post(`/whatsapp/${whatsAppId}/media-upload`, formData);
        }
        if (!attachmentName && whatsApp.greetingMediaAttachment !== null) {
          await api.delete(`/whatsapp/${whatsAppId}/media-upload`);
        }
      } else {
        const { data } = await api.post("/whatsapp", whatsappData);
        if (attachment != null) {
          const formData = new FormData();
          formData.append("file", attachment);
          await api.post(`/whatsapp/${data.id}/media-upload`, formData);
        }
      }
      toast.success(i18n.t("whatsappModal.success"));

      handleClose();
    } catch (err) {
      toastError(err);
    }
  };

  function generateRandomCode(length) {
    const charset =
      "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyvz0123456789";
    let code = "";

    for (let i = 0; i < length; i++) {
      const randomIndex = Math.floor(Math.random() * charset.length);
      code += charset.charAt(randomIndex);
    }
    return code;
  }

  const handleRefreshToken = () => {
    setAutoToken(generateRandomCode(30));
  };

  const handleChangeFlowIdNotPhrase = (e) => {
    setFlowIdNotPhrase(e.target.value);
  };

  const handleChangeFlowIdWelcome = (e) => {
    setFlowIdWelcome(e.target.value);
  };

  const handleChangeFlowIdInactiveTime = (e) => {
    setFlowIdInactiveTime(e.target.value);
  };

  const handleChangeTimeAwaitActiveFlowId = (e) => {
    setTimeAwaitActiveFlowId(e.target.value);
  };

  const handleCopyToken = () => {
    navigator.clipboard.writeText(autoToken); // Copia o token para a área de transferência
    setCopied(true); // Define o estado de cópia como verdadeiro
  };

  const handleSaveSchedules = async (values) => {
    toast.success("Clique em salvar para registar as alterações");
    setSchedules(values);
  };

  const handleClose = () => {
    onClose();
    setWhatsApp(initialState);
    setEnableImportMessage(false);
    // inputFileRef.current.value = null
    setAttachment(null);
    setAttachmentName("");
    setCopied(false);
  };

  const handleTabChange = (event, newValue) => {
    setTab(newValue);
  };

  const handleFileUpload = () => {
    const file = inputFileRef.current.files[0];
    setAttachment(file);
    setAttachmentName(file.name);
    inputFileRef.current.value = null;
  };

  const handleDeleFile = () => {
    setAttachment(null);
    setAttachmentName(null);
  };

  // aviso da importação: fica junto das opções de importação (ou no topo, se o canal for oficial)
  const importAlert = enableImportMessage && (
    <div className={classes.importAlert}>{i18n.t("whatsappModal.form.importAlert")}</div>
  );
  const flowItems = webhooks.map((webhook) => (
    <MenuItem key={webhook.id} value={webhook.id}>
      {webhook.name}
    </MenuItem>
  ));

  return (
    <div className={classes.root}>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="md"
        fullWidth
        scroll="paper"
        classes={{ paper: clsx(p.dialogPaper, classes.dialog) }}
        aria-labelledby="channel-editor-title"
      >
        <Formik
          initialValues={whatsApp}
          enableReinitialize={true}
          validationSchema={isOficial ? SessionSchemaOficial : SessionSchema}
          onSubmit={async (values, actions) => {
            try { await handleSaveWhatsApp(values); } finally { actions.setSubmitting(false); }
          }}
        >
          {({ values, touched, errors, isSubmitting, submitCount, setFieldValue }) => (
            <Form className={p.modalForm}>
              <ChannelValidationNavigation onNavigate={setTab} />
              <ModalTitle
                id="channel-editor-title"
                title={whatsAppId ? "Editar canal" : "Novo canal"}
                subtitle={<>
                  {values.name || "Configure seu canal de atendimento"}{isOficial ? " · WhatsApp API oficial" : isWebchat ? i18n.t("whatsappModal.webchat.subtitle") : " · WhatsApp"}
                  {whatsAppId && !loadingChannel && whatsApp.status === "CONNECTED" && <StatusBadge className={classes.titleBadge} size="sm" status="CONNECTED" label="Conectado" />}
                </>}
                onClose={handleClose}
              />
              {submitCount > 0 && Object.keys(errors).length > 0 && <div role="alert" className={classes.alert}>Confira os campos destacados antes de salvar.</div>}
              <Tabs value={attendanceSections.includes(tab) ? "integrations" : tab} onChange={handleTabChange} variant="scrollable" scrollButtons="auto" className={p.modalTabs} aria-label="Configurações do canal">
                {navigationSections.filter(([key]) => (key !== "credentials" || (isOficial && !isWebchat)) && (key !== "flowbuilder" || (showIntegrations && !isWebchat && user.showFlow === "enabled")) && (key !== "advanced" || !isWebchat)).map(([key, label]) => <Tab key={key} id={`simple-tab-${key}`} value={key} label={label} aria-label={key === "credentials" ? "API oficial · Meta" : key === "flowbuilder" ? "Fluxos de conversa" : label} />)}
                {isWebchat && <Tab key="webchat" id="simple-tab-webchat" value="webchat" label={i18n.t("whatsappModal.webchat.tab")} aria-label={i18n.t("whatsappModal.webchat.tab")} />}
              </Tabs>
              <DialogContent className={clsx(p.modalBody, p.labelsAbove, classes.body)} key={tab}>
                {attendanceSections.includes(tab) && <div className={classes.subnav} role="group" aria-label="Opções de atendimento">{attendanceSections.filter(key => (key !== "schedules" || schedulesEnabled) && (key !== "nps" || !isWebchat) && (key !== "schedules" || !isWebchat)).map(key => <Button key={key} id={`simple-tab-${key}-detail`} type="button" aria-pressed={tab === key} onClick={() => setTab(key)}>{channelSections[key][0]}</Button>)}</div>}
                {channelSections[tab]?.[1] && <p className={classes.lead}>{channelSections[tab][1]}</p>}
                {loadingChannel && <div className={classes.loading} role="status"><CircularProgress size={20} /> Carregando canal…</div>}
                <div style={{ display: loadingChannel ? "none" : undefined }}>
                <TabPanel value={tab} name={"general"}>
                  <FormSection title="Identificação">
                    <div className={classes.nameGrid}>
                      <Field as={TextField} fullWidth label={i18n.t("whatsappModal.form.name")} autoFocus name="name" id="channel-name" error={touched.name && Boolean(errors.name)} helperText={touched.name && errors.name} variant="outlined" size="small" />
                      <Field as={TextField} fullWidth label={i18n.t("connections.table.color")} name="color" id="color" variant="outlined" size="small"
                        InputProps={{ startAdornment: <InputAdornment position="start"><span style={{ backgroundColor: values.color }} className={classes.colorSwatch} /></InputAdornment>, endAdornment: <InputAdornment position="end"><IconButton size="small" aria-label="Escolher cor do canal" onClick={() => setColorPickerModalOpen(true)}><Colorize fontSize="small" /></IconButton></InputAdornment> }} />
                    </div>
                    <ColorPicker open={colorPickerModalOpen} handleClose={() => setColorPickerModalOpen(false)} onChange={color => setFieldValue("color", color)} />
                  </FormSection>
                  {!isWebchat && <FormSection title="Comportamento">
                    <SettingRow label="Canal padrão" description="Use este canal como padrão para novos atendimentos.">
                      <Field as={Switch} color="primary" name="isDefault" checked={values.isDefault} inputProps={{"aria-label":"Canal padrão"}} />
                    </SettingRow>
                    <SettingRow label="Permitir grupos" description="Habilite o atendimento de conversas em grupo neste canal.">
                      <Field as={Switch} color="primary" name="allowGroup" checked={values.allowGroup} inputProps={{"aria-label":"Permitir grupos"}} />
                    </SettingRow>
                    <SettingRow wide label={i18n.t("whatsappModal.form.groupAsTicket")}>
                      <FormControl variant="outlined" size="small" fullWidth>
                        <Field as={Select} id="groupAsTicket" name="groupAsTicket" SelectDisplayProps={{ "aria-label": i18n.t("whatsappModal.form.groupAsTicket") }}>
                          <MenuItem value="disabled">{i18n.t("whatsappModal.menuItem.disabled")}</MenuItem>
                          <MenuItem value="enabled">{i18n.t("whatsappModal.menuItem.enabled")}</MenuItem>
                        </Field>
                      </FormControl>
                    </SettingRow>
                  </FormSection>}
                  <FormSection title="Atalhos" description="Vá direto às outras configurações deste canal.">
                    <div className={classes.shortcuts}>
                      <Button type="button" onClick={() => setTab("integrations")}><HeadsetMicOutlined /><span className={classes.shortcutText}><strong>Filas de atendimento</strong><small>{selectedQueueIds.length ? `${selectedQueueIds.length} fila(s) selecionada(s)` : "Escolha quem recebe as conversas"}</small></span><ArrowForwardRounded /></Button>
                      {showIntegrations && !isWebchat && user.showFlow === "enabled" && <Button type="button" onClick={() => setTab("flowbuilder")}><AccountTreeOutlined /><span className={classes.shortcutText}><strong>Fluxos de conversa</strong><small>Configure a entrada do chatbot</small></span><ArrowForwardRounded /></Button>}
                      {isOficial && <Button type="button" onClick={() => setTab("credentials")}><VpnKeyOutlined /><span className={classes.shortcutText}><strong>API oficial da Meta</strong><small>Conta, número e credenciais</small></span><ArrowForwardRounded /></Button>}
                      <Button type="button" onClick={() => setTab("messages")}><ChatBubbleOutlineRounded /><span className={classes.shortcutText}><strong>Mensagens automáticas</strong><small>Saudação, ausência e encerramento</small></span><ArrowForwardRounded /></Button>
                    </div>
                  </FormSection>
                </TabPanel>

                <TabPanel value={tab} name="credentials">
                  {isOficial && whatsApp?.metaConnectedAt && (
                    <div className={clsx(p.notice, classes.metaBox)}>
                      <strong>{i18n.t("whatsappModal.metaSignup.title")}</strong>
                      <span>{i18n.t(`metaSignup.status.${whatsApp.metaSignupStatus || "ok"}`)}</span>
                      <small>
                        {i18n.t("whatsappModal.metaSignup.review")}: {whatsApp.metaAccountReviewStatus || "-"} · {i18n.t("whatsappModal.metaSignup.quality")}: {whatsApp.metaQualityRating || "-"}
                      </small>
                      {whatsApp.metaSignupStatus === "pin_required" && (
                        <div className={clsx(classes.inlineRow, classes.spaced)}>
                          <TextField id="channel-meta-pin" size="small" variant="outlined" label={i18n.t("whatsappModal.metaSignup.pinLabel")} value={metaPin}
                            onChange={e => setMetaPin(e.target.value.replace(/\D/g, "").slice(0, 6))} />
                          <Button type="button" variant="outlined" disabled={metaPin.length !== 6} onClick={async () => {
                            try {
                              const { data } = await api.post(`/meta-signup/${whatsAppId}/register`, { pin: metaPin });
                              setWhatsApp({ ...whatsApp, metaSignupStatus: data.status });
                              toast.success(i18n.t(`metaSignup.status.${data.status}`));
                            } catch (err) { toastError(err); }
                          }}>{i18n.t("whatsappModal.metaSignup.sendPin")}</Button>
                        </div>
                      )}
                      <small>{i18n.t("whatsappModal.metaSignup.reconnectHint")}</small>
                    </div>
                  )}
                  {isOficial && whatsApp?.metaCoexistence && (
                    <div className={clsx(p.notice, classes.metaBox)}>
                      <strong>{i18n.t("whatsappModal.metaSignup.coexTitle")}</strong>
                      <span>{i18n.t(`metaSignup.coexSync.${whatsApp.metaSyncStatus || "none"}`, { count: whatsApp.metaSyncMessages || 0 })}</span>
                      <small>{i18n.t("whatsappModal.metaSignup.coexHint")}</small>
                      {/* a Meta só aceita o pedido de contatos/histórico nas 24 h após a conexão */}
                      {whatsApp.metaConnectedAt && Date.now() - new Date(whatsApp.metaConnectedAt).getTime() < 24 * 3600 * 1000 && (
                        <div className={clsx(classes.inlineRow, classes.spaced)}>
                          <Button type="button" variant="outlined" onClick={async () => {
                            try {
                              const { data } = await api.post(`/meta-signup/${whatsAppId}/sync`);
                              setWhatsApp({ ...whatsApp, metaSyncStatus: data.status });
                              toast.success(i18n.t(`metaSignup.coexSync.${data.status}`, { count: whatsApp.metaSyncMessages || 0 }));
                            } catch (err) { toastError(err); }
                          }}>{i18n.t("whatsappModal.metaSignup.coexResync")}</Button>
                        </div>
                      )}
                    </div>
                  )}
                  {isOficial && (
                    <FormSection title="Acesso à Meta">
                      <div className={p.grid2}>
                        <Field
                          fullWidth
                          as={TextField}
                          label={i18n.t("whatsappModal.form.business_id")}
                          name="business_id"
                          id="channel-business_id"
                          placeholder="Ex.: 289012938409821"
                          InputLabelProps={{
                            shrink: true,
                          }}
                          error={
                            touched.business_id &&
                            Boolean(errors.business_id)
                          }
                          helperText={
                            touched.business_id && errors.business_id
                          }
                          variant="outlined"
                          size="small"
                          required
                        />
                        <Field
                          fullWidth
                          as={TextField}
                          label={i18n.t("whatsappModal.form.send_token")}
                          name="send_token"
                          id="channel-send_token"
                          type="password"
                          autoComplete="off"
                          placeholder="EAAG..."
                          InputLabelProps={{
                            shrink: true,
                          }}
                          error={
                            touched.send_token && Boolean(errors.send_token)
                          }
                          helperText={
                            touched.send_token && errors.send_token
                          }
                          variant="outlined"
                          size="small"
                          required
                        />
                      </div>
                      <details className={classes.details}><summary>Buscar conta e número na Meta</summary><MetaBusinessAssets whatsappId={whatsAppId} compact /></details>
                      <details className={classes.details}><summary>Onde encontrar essas credenciais?</summary><p>{i18n.t("metaBusinessAssets.credentialsHelp")}</p><p>{i18n.t("metaBusinessAssets.tokenHelp")}</p></details>
                    </FormSection>
                  )}
                  {!isWebchat && <FormSection title="Conta e número do WhatsApp" description="Confira os identificadores da conta conectada ou use a busca acima para preenchê-los.">
                    <div className={p.grid2}>
                      <Field
                        fullWidth
                        as={TextField}
                        label={i18n.t(
                          "whatsappModal.form.phone_number_id"
                        )}
                        name="phone_number_id"
                        id="channel-phone_number_id"
                        placeholder="Ex.: 106540352242922"
                        InputLabelProps={{
                          shrink: true,
                        }}
                        error={
                          touched.phone_number_id &&
                          Boolean(errors.phone_number_id)
                        }
                        helperText={
                          touched.phone_number_id && errors.phone_number_id
                        }
                        variant="outlined"
                        size="small"
                        required
                      />
                      <Field
                        fullWidth
                        as={TextField}
                        label={i18n.t("whatsappModal.form.waba_id")}
                        name="waba_id"
                        id="channel-waba_id"
                        placeholder="Ex.: 102290129340398"
                        InputLabelProps={{
                          shrink: true,
                        }}
                        error={touched.waba_id && Boolean(errors.waba_id)}
                        helperText={
                          touched.waba_id && errors.waba_id
                        }
                        variant="outlined"
                        size="small"
                        required
                      />
                      <Field
                        fullWidth
                        as={TextField}
                        label={i18n.t("whatsappModal.form.phone_number")}
                        name="phone_number"
                        id="channel-phone_number"
                        placeholder="Ex.: 5511999998888"
                        InputLabelProps={{
                          shrink: true,
                        }}
                        error={
                          touched.phone_number &&
                          Boolean(errors.phone_number)
                        }
                        helperText={
                          touched.phone_number && errors.phone_number
                        }
                        variant="outlined"
                        size="small"
                        required
                      />
                    </div>
                    {whatsApp.waba_webhook && (
                      <div className={clsx(classes.inlineRow, classes.wrapOnPhone, classes.spaced)}>
                        <TextField id="channel-webhook" fullWidth variant="outlined" size="small" label="URL de callback (webhook)" value={whatsApp.waba_webhook} InputProps={{ readOnly: true }} />
                        <Button type="button" variant="outlined" onClick={async () => { try { await navigator.clipboard.writeText(whatsApp.waba_webhook); toast.success("Webhook copiado"); } catch (err) { toast.error("Selecione e copie a URL acima."); } }}>Copiar webhook</Button>
                      </div>
                    )}
                  </FormSection>}
                </TabPanel>

                <TabPanel value={tab} name="advanced">
                  {isOficial && enableImportMessage && <div style={{ marginBottom: 20 }}>{importAlert}</div>}
                  {!isOficial && !isWebchat && (
                    <FormSection title="Importação de mensagens">
                      <SettingRow label={i18n.t("whatsappModal.form.importOldMessagesEnable")}>
                        <Switch
                          checked={enableImportMessage}
                          onChange={handleEnableImportMessage}
                          name="importOldMessagesEnable"
                          color="primary"
                          inputProps={{ "aria-label": i18n.t("whatsappModal.form.importOldMessagesEnable") }}
                        />
                      </SettingRow>
                      {enableImportMessage ? (
                        <>
                          <SettingRow label={i18n.t("whatsappModal.form.importOldMessagesGroups")}>
                            <Switch
                              checked={importOldMessagesGroups}
                              onChange={(e) =>
                                setImportOldMessagesGroups(
                                  e.target.checked
                                )
                              }
                              name="importOldMessagesGroups"
                              color="primary"
                              inputProps={{ "aria-label": i18n.t("whatsappModal.form.importOldMessagesGroups") }}
                            />
                          </SettingRow>
                          <SettingRow label={i18n.t("whatsappModal.form.closedTicketsPostImported")}>
                            <Switch
                              checked={closedTicketsPostImported}
                              onChange={(e) =>
                                setClosedTicketsPostImported(
                                  e.target.checked
                                )
                              }
                              name="closedTicketsPostImported"
                              color="primary"
                              inputProps={{ "aria-label": i18n.t("whatsappModal.form.closedTicketsPostImported") }}
                            />
                          </SettingRow>
                          <div className={clsx(p.grid2, classes.spaced)}>
                            <Field
                              fullWidth
                              as={TextField}
                              label={i18n.t(
                                "whatsappModal.form.importOldMessages"
                              )}
                              type="datetime-local"
                              name="importOldMessages"
                              id="channel-importOldMessages"
                              inputProps={{
                                max: moment()
                                  .add(0, "minutes")
                                  .format("YYYY-MM-DDTHH:mm"),
                                min: moment()
                                  .add(-2, "years")
                                  .format("YYYY-MM-DDTHH:mm"),
                              }}
                              //min="2022-11-06T22:22:55"
                              InputLabelProps={{
                                shrink: true,
                              }}
                              error={
                                touched.importOldMessages &&
                                Boolean(errors.importOldMessages)
                              }
                              helperText={
                                touched.importOldMessages &&
                                errors.importOldMessages
                              }
                              variant="outlined"
                              size="small"
                              value={moment(importOldMessages).format(
                                "YYYY-MM-DDTHH:mm"
                              )}
                              onChange={(e) => {
                                setImportOldMessages(e.target.value);
                              }}
                            />
                            <Field
                              fullWidth
                              as={TextField}
                              label={i18n.t(
                                "whatsappModal.form.importRecentMessages"
                              )}
                              type="datetime-local"
                              name="importRecentMessages"
                              id="channel-importRecentMessages"
                              inputProps={{
                                max: moment()
                                  .add(0, "minutes")
                                  .format("YYYY-MM-DDTHH:mm"),
                                min: moment(importOldMessages).format(
                                  "YYYY-MM-DDTHH:mm"
                                ),
                              }}
                              //min="2022-11-06T22:22:55"
                              InputLabelProps={{
                                shrink: true,
                              }}
                              error={
                                touched.importRecentMessages &&
                                Boolean(errors.importRecentMessages)
                              }
                              helperText={
                                touched.importRecentMessages &&
                                errors.importRecentMessages
                              }
                              variant="outlined"
                              size="small"
                              value={moment(importRecentMessages).format(
                                "YYYY-MM-DDTHH:mm"
                              )}
                              onChange={(e) => {
                                setImportRecentMessages(e.target.value);
                              }}
                            />
                            <FormControl
                              variant="outlined"
                              size="small"
                              className={p.span2}
                              fullWidth
                            >
                              <InputLabel id="queueIdImportMessages-selection-label">
                                {i18n.t(
                                  "whatsappModal.form.queueIdImportMessages"
                                )}
                              </InputLabel>
                              <Field
                                as={Select}
                                name="queueIdImportMessages"
                                id="queueIdImportMessages"
                                value={values.queueIdImportMessages || "0"}
                                required={enableImportMessage}
                                label={i18n.t(
                                  "whatsappModal.form.queueIdImportMessages"
                                )}
                                placeholder={i18n.t(
                                  "whatsappModal.form.queueIdImportMessages"
                                )}
                                labelId="queueIdImportMessages-selection-label"
                              >
                                <MenuItem value={0}>&nbsp;</MenuItem>
                                {queues.map((queue) => (
                                  <MenuItem key={queue.id} value={queue.id}>
                                    {queue.name}
                                  </MenuItem>
                                ))}
                              </Field>
                            </FormControl>
                          </div>
                        </>
                      ) : null}
                      {importAlert}
                    </FormSection>
                  )}

                  {/* TOKEN */}
                  {!isWebchat && <FormSection title="Chave da API">
                    <div className={classes.inlineRow}>
                      <Field
                        as={TextField}
                        label={i18n.t("whatsappModal.form.token")}
                        type="password"
                        fullWidth
                        // name="token"
                        id="channel-token"
                        value={autoToken}
                        variant="outlined"
                        size="small"
                        disabled
                      />
                      <Tooltip title="Gerar nova chave da API">
                        <span>
                          <IconButton
                            onClick={handleRefreshToken}
                            aria-label="Gerar nova chave da API"
                            disabled={isSubmitting || loadingChannel}
                          >
                            <Autorenew fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title={copied ? "Chave copiada" : "Copiar chave da API"}>
                        <IconButton
                          onClick={handleCopyToken}
                          aria-label="Copiar chave da API"
                          className={copied ? classes.copied : undefined}
                        >
                          <FileCopy fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </div>
                  </FormSection>}
                </TabPanel>

                <TabPanel value={tab} name="integrations">
                  {!loadingChannel && <ChannelRoutingPanel
                    queues={queues} selectedQueueIds={selectedQueueIds} onQueueChange={handleChangeQueue}
                    integrations={integrations} showIntegrations={showIntegrations && !isWebchat} showOpenAi={showOpenAi && !isWebchat}
                    prompts={prompts} selectedPrompt={selectedPrompt} onPromptChange={value => handleChangePrompt({target:{value}})}
                    triggerIntegrationOnClose={triggerIntegrationOnClose} onCloseToggle={setTriggerIntegrationOnClose}
                    integrationTypeId={integrationTypeId} onCloseIntegrationChange={setIntegrationTypeId} showWavoipCall={showWavoipCall && !isWebchat}
                  />}
                </TabPanel>

                <TabPanel value={tab} name={"messages"}>
                  {/* Webchat (Etapa 1): sem bot, saudação, ausência e horário nunca são usados — só o encerramento. */}
                  {!isWebchat && <FormSection title="Mídia da saudação" description="Imagem ou vídeo enviado junto com a mensagem de saudação.">
                    <div className={classes.uploadRow}>
                      <input
                        type="file"
                        accept="video/*,image/*"
                        ref={inputFileRef}
                        style={{ display: "none" }}
                        onChange={handleFileUpload}
                      />
                      <Button
                        variant="outlined"
                        onClick={() => inputFileRef.current.click()}
                      >
                        {i18n.t("userModal.buttons.addImage")}
                      </Button>
                      {attachmentName && (
                        <Chip
                          variant="outlined"
                          label={attachmentName}
                          onDelete={handleDeleFile}
                          deleteIcon={<DeleteOutlineIcon aria-label="Remover mídia da saudação" />}
                        />
                      )}
                    </div>
                  </FormSection>}

                  <FormSection title={isWebchat ? i18n.t("whatsappModal.webchat.closingSection") : "Saudação e conclusão"}>
                    <div className={p.grid2}>
                      {/* MENSAGEM DE SAUDAÇÃO */}
                      {!isWebchat && <ChannelMessageField name="greetingMessage" label={i18n.t("whatsappModal.form.greetingMessage")} />}
                      {/* MENSAGEM DE CONCLUSÃO */}
                      <ChannelMessageField name="complationMessage" label={i18n.t("whatsappModal.form.complationMessage")} />
                    </div>
                  </FormSection>

                  {!isWebchat && <FormSection title="Fora de expediente e férias coletivas" description="As datas definem o período das férias coletivas.">
                    <div className={p.grid2}>
                      {/* MENSAGEM DE FORA DE EXPEDIENTE */}
                      <ChannelMessageField name="outOfHoursMessage" label={i18n.t("whatsappModal.form.outOfHoursMessage")} />
                      {/* MENSAGEM DE FÉRIAS COLETIVAS */}
                      <ChannelMessageField name="collectiveVacationMessage" label={i18n.t("whatsappModal.form.collectiveVacationMessage")} />
                      <Field
                        fullWidth
                        as={TextField}
                        label={i18n.t(
                          "whatsappModal.form.collectiveVacationStart"
                        )}
                        type="date"
                        name="collectiveVacationStart"
                        id="channel-collectiveVacationStart"
                        required={
                          values.collectiveVacationMessage?.length > 0
                        }
                        inputProps={{
                          min: moment().add(-10, "days").format("YYYY-MM-DD"),
                        }}
                        //min="2022-11-06T22:22:55"
                        InputLabelProps={{
                          shrink: true,
                        }}
                        error={
                          touched.collectiveVacationStart &&
                          Boolean(errors.collectiveVacationStart)
                        }
                        helperText={
                          touched.collectiveVacationStart &&
                          errors.collectiveVacationStart
                        }
                        variant="outlined"
                        size="small"
                      />
                      <Field
                        fullWidth
                        as={TextField}
                        label={i18n.t(
                          "whatsappModal.form.collectiveVacationEnd"
                        )}
                        type="date"
                        name="collectiveVacationEnd"
                        id="channel-collectiveVacationEnd"
                        required={
                          values.collectiveVacationMessage?.length > 0
                        }
                        inputProps={{
                          min: moment().add(-10, "days").format("YYYY-MM-DD"),
                        }}
                        //min="2022-11-06T22:22:55"
                        InputLabelProps={{
                          shrink: true,
                        }}
                        error={
                          touched.collectiveVacationEnd &&
                          Boolean(errors.collectiveVacationEnd)
                        }
                        helperText={
                          touched.collectiveVacationEnd &&
                          errors.collectiveVacationEnd
                        }
                        variant="outlined"
                        size="small"
                      />
                    </div>
                  </FormSection>}
                </TabPanel>

                <TabPanel
                  value={tab}
                  name={"chatbot"}
                  aria-labelledby="simple-tab-chatbot-detail"
                >
                  <FormSection title="Menu e novos atendimentos">
                    <div className={p.grid2}>
                      {/* TEMPO PARA CRIAR NOVO TICKET */}
                      <Field
                        as={TextField}
                        label={i18n.t(
                          "whatsappModal.form.timeCreateNewTicket"
                        )}
                        fullWidth
                        name="timeCreateNewTicket"
                        id="channel-timeCreateNewTicket"
                        variant="outlined"
                        size="small"
                        error={
                          touched.timeCreateNewTicket &&
                          Boolean(errors.timeCreateNewTicket)
                        }
                        helperText={
                          touched.timeCreateNewTicket &&
                          errors.timeCreateNewTicket
                        }
                      />
                      {/* QUANTIDADE MÁXIMA DE VEZES QUE O CHATBOT VAI SER ENVIADO */}
                      <Field
                        as={TextField}
                        label={i18n.t("whatsappModal.form.maxUseBotQueues")}
                        fullWidth
                        name="maxUseBotQueues"
                        id="channel-maxUseBotQueues"
                        variant="outlined"
                        size="small"
                        error={
                          touched.maxUseBotQueues &&
                          Boolean(errors.maxUseBotQueues)
                        }
                        helperText={
                          touched.maxUseBotQueues && errors.maxUseBotQueues
                        }
                      />
                      {/* TEMPO PARA ENVIO DO CHATBOT */}
                      <Field
                        as={TextField}
                        label={i18n.t("whatsappModal.form.timeUseBotQueues")}
                        fullWidth
                        name="timeUseBotQueues"
                        id="channel-timeUseBotQueues"
                        variant="outlined"
                        size="small"
                        error={
                          touched.timeUseBotQueues &&
                          Boolean(errors.timeUseBotQueues)
                        }
                        helperText={
                          touched.timeUseBotQueues && errors.timeUseBotQueues
                        }
                      />
                      {/* TEMPO PARA RETORNAR A FILA */}
                      <Field
                        as={TextField}
                        label={i18n.t("whatsappModal.form.timeToReturnQueue")}
                        fullWidth
                        name="timeToReturnQueue"
                        id="channel-timeToReturnQueue"
                        variant="outlined"
                        size="small"
                        error={
                          touched.timeToReturnQueue &&
                          Boolean(errors.timeToReturnQueue)
                        }
                        helperText={
                          touched.timeToReturnQueue &&
                          errors.timeToReturnQueue
                        }
                      />
                    </div>
                  </FormSection>

                  <FormSection title="Encerramento automático">
                    <div className={p.grid2}>
                      {/* ENCERRAR CHATS ABERTOS APÓS X HORAS */}
                      <Field
                        as={TextField}
                        label={i18n.t("whatsappModal.form.expiresTicket")}
                        fullWidth
                        name="expiresTicket"
                        id="channel-expiresTicket"
                        required={values.timeInactiveMessage > 0}
                        variant="outlined"
                        size="small"
                        error={
                          touched.expiresTicket &&
                          Boolean(errors.expiresTicket)
                        }
                        helperText={
                          touched.expiresTicket && errors.expiresTicket
                        }
                      />
                      <FormControl
                        variant="outlined"
                        size="small"
                        fullWidth
                      >
                        <InputLabel id="whenExpiresTicket-selection-label">
                          {i18n.t("whatsappModal.form.whenExpiresTicket")}
                        </InputLabel>
                        <Field
                          as={Select}
                          label={i18n.t(
                            "whatsappModal.form.whenExpiresTicket"
                          )}
                          placeholder={i18n.t(
                            "whatsappModal.form.whenExpiresTicket"
                          )}
                          labelId="whenExpiresTicket-selection-label"
                          id="whenExpiresTicket"
                          name="whenExpiresTicket"
                        >
                          <MenuItem value={"0"}>
                            {i18n.t(
                              "whatsappModal.form.closeLastMessageOptions1"
                            )}
                          </MenuItem>
                          <MenuItem value={"1"}>
                            {i18n.t(
                              "whatsappModal.form.closeLastMessageOptions2"
                            )}
                          </MenuItem>
                        </Field>
                      </FormControl>
                      {/* MENSAGEM POR INATIVIDADE*/}
                      <div className={p.span2}>
                        <ChannelMessageField name="expiresInactiveMessage" label={i18n.t("whatsappModal.form.expiresInactiveMessage")} />
                      </div>
                    </div>
                  </FormSection>

                  <FormSection title="Aviso de inatividade">
                    <div className={p.grid2}>
                      {/* MENSAGEM POR INATIVIDADE*/}
                      <div className={p.span2}>
                        <ChannelMessageField name="inactiveMessage" label={i18n.t("whatsappModal.form.inactiveMessage")} />
                      </div>
                      {/* TEMPO PARA ENVIO DE MENSAGEM POR INATIVIDADE */}
                      <Field
                        as={TextField}
                        label={i18n.t("whatsappModal.form.timeInactiveMessage")}
                        fullWidth
                        name="timeInactiveMessage"
                        id="channel-timeInactiveMessage"
                        variant="outlined"
                        size="small"
                        error={
                          touched.timeInactiveMessage &&
                          Boolean(errors.timeInactiveMessage)
                        }
                        helperText={
                          touched.timeInactiveMessage &&
                          errors.timeInactiveMessage
                        }
                      />
                      {/* QUANTIDADE MÁXIMA DE VEZES QUE O FLOW DE INATIVIDADE VAI SER ENVIADO */}
                      <Field
                        as={TextField}
                        label={i18n.t(
                          "whatsappModal.form.maxUseInactiveTime"
                        )}
                        fullWidth
                        name="maxUseInactiveTime"
                        id="channel-maxUseInactiveTime"
                        variant="outlined"
                        size="small"
                        error={
                          touched.maxUseInactiveTime &&
                          Boolean(errors.maxUseInactiveTime)
                        }
                        helperText={
                          touched.maxUseInactiveTime &&
                          errors.maxUseInactiveTime
                        }
                      />
                      {user.showFlow === "enabled" && (
                        <>
                          {/* TEMPO PARA ENVIO DO FLOW DE INATIVIDADE */}
                          <Field
                            as={TextField}
                            label={i18n.t("whatsappModal.form.flowInactiveTime")}
                            fullWidth
                            name="flowInactiveTime"
                            id="channel-flowInactiveTime"
                            variant="outlined"
                            size="small"
                            error={
                              touched.flowIdInactiveTime &&
                              Boolean(errors.flowIdInactiveTime)
                            }
                            helperText={
                              touched.flowIdInactiveTime &&
                              errors.flowIdInactiveTime
                            }
                          />
                          {/* FLUXO DE INATIVIDADE */}
                          <FormControl
                            variant="outlined"
                            size="small"
                            fullWidth
                          >
                            <InputLabel id="flowIdInactiveTime-selection-label">Fluxo de inatividade</InputLabel>
                            <Select
                              name="flowIdInactiveTime"
                              value={flowIdInactiveTime || ""}
                              onChange={handleChangeFlowIdInactiveTime}
                              id="flowIdInactiveTime"
                              variant="outlined"
                              labelId="flowIdInactiveTime-selection-label"
                            >
                              <MenuItem value={null}>{"Desabilitado"}</MenuItem>
                              {flowItems}
                            </Select>
                          </FormControl>
                        </>
                      )}
                      {/* timeAwaitActiveFlow / timeAwaitActiveFlowId seguem desativados (estavam comentados; ver histórico do arquivo) */}
                    </div>
                  </FormSection>
                </TabPanel>

                {/* NPS */}
                <TabPanel
                  value={tab}
                  name={"nps"}
                  aria-labelledby="simple-tab-nps-detail"
                >
                  <FormSection>
                    <div className={p.grid2}>
                      {/* MENSAGEM DE AVALIAÇAO*/}
                      <div className={p.span2}>
                        <ChannelMessageField name="ratingMessage" label={i18n.t("whatsappModal.form.ratingMessage")} />
                      </div>
                      {/* QUANTIDADE MÁXIMA DE VEZES QUE O NPS VAI SER ENVIADO */}
                      <Field
                        as={TextField}
                        label={i18n.t("whatsappModal.form.maxUseBotQueuesNPS")}
                        fullWidth
                        name="maxUseBotQueuesNPS"
                        id="channel-maxUseBotQueuesNPS"
                        variant="outlined"
                        size="small"
                        error={
                          touched.maxUseBotQueuesNPS &&
                          Boolean(errors.maxUseBotQueuesNPS)
                        }
                        helperText={
                          touched.maxUseBotQueuesNPS &&
                          errors.maxUseBotQueuesNPS
                        }
                      />
                      {/* ENCERRAR CHATS NPS APÓS X Minutos */}
                      <Field
                        as={TextField}
                        label={i18n.t("whatsappModal.form.expiresTicketNPS")}
                        fullWidth
                        name="expiresTicketNPS"
                        id="channel-expiresTicketNPS"
                        variant="outlined"
                        size="small"
                        error={
                          touched.expiresTicketNPS &&
                          Boolean(errors.expiresTicketNPS)
                        }
                        helperText={
                          touched.expiresTicketNPS && errors.expiresTicketNPS
                        }
                      />
                    </div>
                  </FormSection>
                </TabPanel>

                {/* Flowbuilder */}
                {showIntegrations && !isWebchat && user.showFlow === "enabled" && (
                  <TabPanel value={tab} name={"flowbuilder"}>
                    <div className={classes.leadAction}>
                      <Button component="a" href="/flowbuilders" target="_blank" rel="noopener noreferrer" variant="outlined" size="small">Abrir editor de fluxos ↗</Button>
                    </div>
                    <FormSection title="Receber novos contatos" description="Escolha o fluxo iniciado quando alguém que ainda não está nos seus contatos envia uma mensagem.">
                      <FormControl
                        variant="outlined"
                        size="small"
                        fullWidth
                      >
                        <Select
                          inputProps={{ "aria-label": "Fluxo para novos contatos" }}
                          name="flowIdNotPhrase"
                          value={flowIdNotPhrase || ""}
                          onChange={handleChangeFlowIdNotPhrase}
                          id="flowIdNotPhrase"
                          variant="outlined"
                          labelId="flowIdNotPhrase-selection-label"
                        >
                          <MenuItem value={null}>{"Desabilitado"}</MenuItem>
                          {flowItems}
                        </Select>
                      </FormControl>
                    </FormSection>
                    <FormSection title="Resposta padrão após encerrar um atendimento" description="Escolha o fluxo para mensagens sem uma palavra-chave correspondente, quando o atendimento anterior já está encerrado.">
                      <FormControl
                        variant="outlined"
                        size="small"
                        fullWidth
                      >
                        <Select
                          inputProps={{ "aria-label": "Fluxo de resposta padrão" }}
                          name="flowIdWelcome"
                          value={flowIdWelcome || ""}
                          onChange={handleChangeFlowIdWelcome}
                          id="flowIdWelcome"
                          variant="outlined"
                          labelId="flowIdWelcome-selection-label"
                        >
                          <MenuItem value={null}>{"Desabilitado"}</MenuItem>
                          {flowItems}
                        </Select>
                      </FormControl>
                    </FormSection>
                  </TabPanel>
                )}

                {/* Webchat */}
                {isWebchat && (
                  <TabPanel value={tab} name={"webchat"}>
                    <WebchatSettingsPanel whatsAppId={whatsAppId} values={values} setFieldValue={setFieldValue} />
                  </TabPanel>
                )}

                {/* Schedules */}
                <TabPanel
                  value={tab}
                  name={"schedules"}
                  aria-labelledby="simple-tab-schedules-detail"
                >
                  {tab === "schedules" && !isWebchat && (
                    <SchedulesForm
                      loading={false}
                      onSubmit={handleSaveSchedules}
                      initialValues={schedules}
                      embedded onChange={setSchedules}
                    />
                  )}
                </TabPanel>
                </div>
              </DialogContent>
              <DialogActions>
                <span className={classes.saveHint}>As alterações são aplicadas ao salvar.</span>
                <Button
                  onClick={handleClose}
                  disabled={isSubmitting || loadingChannel}
                >
                  {i18n.t("whatsappModal.buttons.cancel")}
                </Button>
                <Button
                  type="submit"
                  color="primary"
                  disabled={isSubmitting || loadingChannel}
                  variant="contained"
                  className={classes.btnWrapper}
                >
                  {whatsAppId
                    ? i18n.t("whatsappModal.buttons.okEdit")
                    : i18n.t("whatsappModal.buttons.okAdd")}
                  {isSubmitting && (
                    <CircularProgress
                      size={24}
                      className={classes.buttonProgress}
                    />
                  )}
                </Button>
              </DialogActions>
            </Form>
          )}
        </Formik>
      </Dialog>
    </div >
  );
};

export default React.memo(WhatsAppModal);
